import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { extractErrorMessage } from './ticketMutations'
import type { ClientDashboard, ClientDashboardChanges, EligibleCompany, HelpDeskHealth, HelpDeskPeriod } from '@/types/clientDashboard'
import type { ClientLinearIssues } from '@/types/linearClient'

export const clientDashboardKeys = {
  all: ['client-dashboards'] as const,
  list: (mine: boolean) => [...clientDashboardKeys.all, 'list', { mine }] as const,
  eligible: () => [...clientDashboardKeys.all, 'eligible'] as const,
  detail: (companyId: string) => [...clientDashboardKeys.all, 'detail', companyId] as const,
  helpDesk: (companyId: string, period: HelpDeskPeriod) => [...clientDashboardKeys.all, 'help-desk', companyId, period] as const,
  linearIssues: (companyId: string) => [...clientDashboardKeys.all, 'linear-issues', companyId] as const,
}

export function useClientDashboards(mine: boolean) {
  return useQuery({
    queryKey: clientDashboardKeys.list(mine),
    queryFn: () => api<{ data: ClientDashboard[] }>('/client-dashboards', { query: mine ? { mine: 'true' } : undefined }),
  })
}

export function useEligibleCompanies(enabled: boolean) {
  return useQuery({
    queryKey: clientDashboardKeys.eligible(),
    queryFn: () => api<{ data: EligibleCompany[] }>('/client-dashboards/eligible-companies'),
    enabled,
  })
}

export function useClientDashboard(companyId: string) {
  return useQuery({
    queryKey: clientDashboardKeys.detail(companyId),
    queryFn: () => api<{ data: ClientDashboard }>(`/client-dashboards/${companyId}`),
    retry: false,
  })
}

export function useAddClientDashboards() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (companyIds: string[]) =>
      api<{ data: ClientDashboard[] }>('/client-dashboards', { method: 'POST', body: { companyIds } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all }),
  })
}

export function useUpdateClientDashboard(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (changes: ClientDashboardChanges) =>
      api<{ data: ClientDashboard }>(`/client-dashboards/${companyId}`, { method: 'PATCH', body: changes }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all }),
  })
}

// Drop every query of the removed client rather than refetching them, which would 404 while its page is still open.
export function useRemoveClientDashboard(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api(`/client-dashboards/${companyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: clientDashboardKeys.all, predicate: query => query.queryKey[2] === companyId })
      void queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all, predicate: query => query.queryKey[2] !== companyId })
    },
  })
}

const PERIOD_DAYS: Record<Exclude<HelpDeskPeriod, 'all'>, number> = { '3m': 90, '12m': 365 }
const ALL_TIME_FROM = '2000-01-01T00:00:00.000Z'

export function useClientHelpDesk(companyId: string, period: HelpDeskPeriod) {
  return useQuery({
    queryKey: clientDashboardKeys.helpDesk(companyId, period),
    // Figures change with every ticket edit, reply and promotion made elsewhere, so reload whenever the tab opens.
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: () => {
      const from = period === 'all' ? ALL_TIME_FROM : new Date(Date.now() - PERIOD_DAYS[period] * 86400000).toISOString()
      return api<{ data: HelpDeskHealth }>(`/client-dashboards/${companyId}/help-desk`, { query: { from } })
    },
  })
}

export function useClientLinearIssues(companyId: string) {
  return useQuery({
    queryKey: clientDashboardKeys.linearIssues(companyId),
    queryFn: () => api<{ data: ClientLinearIssues }>(`/client-dashboards/${companyId}/linear-issues`),
  })
}

// Re-read this client's issues from Linear now; the response replaces the tab's data.
export function useRefreshClientLinearIssues(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ data: ClientLinearIssues }>(`/client-dashboards/${companyId}/linear-issues/refresh`, { method: 'POST' }),
    onSuccess: data => {
      queryClient.setQueryData(clientDashboardKeys.linearIssues(companyId), data)
      toast.success('Up to date with Linear')
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't refresh from Linear")),
  })
}
